/* ---------------------------------------------------------------------------
   <m-cine-hero>

   Writes a single custom property, --p (0 to 1), on itself while the stage is
   on screen. Every effect in cine-hero.css derives from that one number, so a
   frame of scrolling costs one rect read and one property write on one
   element, regardless of how much is moving.

   Also: splits the display heading into staggered units once, sizes it to fit
   the width, loads the video only when it should be loaded, and only plays it
   while it is on screen.

   EVERYTHING IS INSIDE THE GUARD BELOW ON PURPOSE. The section emits its own
   <script src> tag, so a page with five Cinematic heroes runs this file five
   times. A top-level `class MCineHero` threw
   "SyntaxError: Identifier 'MCineHero' has already been declared" on runs two
   to five, four exceptions in the console on a page that otherwise looked
   fine. A class expression inside the guard creates no global binding, so
   re-running the file is a no-op.
   --------------------------------------------------------------------------- */

if (!customElements.get('m-cine-hero')) {
  customElements.define(
    'm-cine-hero',
    class extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.stage = this.querySelector('[data-stage]');
        this.pinned = this.querySelector('[data-pinned]') || this.querySelector('.cine-hero__viewport');
        this.display = this.querySelector('[data-display-split]');
        this.displayWrap = this.querySelector('.cine-hero__display-wrap');
        this.video = this.querySelector('video');
        this.source = this.video ? this.video.querySelector('source[data-src]') : null;
        this.ticking = false;
        this.frame = null;
        this.pWritten = false;

        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        /* Save-Data is the visitor telling the browser to spare their
           bandwidth. A decorative background video is exactly what it means. */
        var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        this.saveData = !!(conn && conn.saveData);

        this.desktopOnlyVideo = this.video ? this.video.dataset.desktopOnly === 'true' : false;
        this.mqDesktop = window.matchMedia('(min-width: 768px)');

        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);
        this.loadVideo = this.loadVideo.bind(this);

        /* Reduced motion: no stage, no scroll driver, and no split. The heading
           stays as one piece of plain text. The poster or mobile image still
           shows, and the video still loads and plays, because a muted
           background loop is not the motion the setting is about. */
        if (this.reduced) {
          this.style.setProperty('--p', '0');
        } else {
          this.splitDisplay();
        }

        /* Shrink-to-fit runs whether or not the letters are split, so a reduced
           motion visitor gets the same layout as everyone else. */
        this.fitDisplay();
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.fitDisplay(true));
        }
        if (this.displayWrap) {
          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.fitDisplay());
            this.resizeObserver.observe(this.displayWrap);
          } else {
            /* older Safari. fitDisplay returns immediately unless the width has
               actually changed, so this costs one read per resize. */
            this.onWinResize = () => this.fitDisplay();
            window.addEventListener('resize', this.onWinResize, { passive: true });
          }
        }

        if (!('IntersectionObserver' in window)) {
          /* no observer: the scroll driver never runs, so pin the heading open
             rather than leave it mid-animation */
          this.classList.add('is-static');
          this.loadVideo();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.classList.add('is-active');
                this.loadVideo();
                this.playVideo();
                if (!this.reduced) {
                  window.addEventListener('scroll', this.onScroll, { passive: true });
                  window.addEventListener('resize', this.onScroll, { passive: true });
                  this.update();
                }
              } else {
                this.classList.remove('is-active');
                this.pauseVideo();
                window.removeEventListener('scroll', this.onScroll);
                window.removeEventListener('resize', this.onScroll);
              }
            });
          },
          { rootMargin: '10% 0px 10% 0px' }
        );

        this.observer.observe(this);

        if (this.desktopOnlyVideo) {
          if (this.mqDesktop.addEventListener) this.mqDesktop.addEventListener('change', this.loadVideo);
          else if (this.mqDesktop.addListener) this.mqDesktop.addListener(this.loadVideo);
        }

        /* Failsafe. The display heading can be hidden until progress is
           written, so a callback that never fires would mean a heading that
           never appears. Re-adding a listener that is already attached is a
           no-op, so this is safe even if the observer fires a moment later. */
        if (!this.reduced) {
          this.safety = setTimeout(() => {
            if (this.pWritten) return;
            var rect = this.getBoundingClientRect();
            var vh = window.innerHeight || document.documentElement.clientHeight;
            if (rect.top < vh && rect.bottom > 0) {
              window.addEventListener('scroll', this.onScroll, { passive: true });
              window.addEventListener('resize', this.onScroll, { passive: true });
              this.update();
            }
          }, 2000);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.onWinResize) window.removeEventListener('resize', this.onWinResize);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.safety) clearTimeout(this.safety);
        this.initialised = false;
      }

      /* ---- progress ----------------------------------------------------- */

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        this.frame = requestAnimationFrame(this.update);
      }

      /* 0 when the stage's top edge reaches the top of the viewport, 1 at the
         moment the pinned child is about to unstick.

         The denominator is the stage height minus the PINNED CHILD's height,
         not minus the window height. Those are the same thing only when the
         hero is a full 100vh tall. At the mobile default of 90 they differ,
         and using the window height ended the animation at about 80% of the
         pin, so the last stretch of scrolling moved nothing. Measured before
         the fix: p reached 0.64 at the end of the stage instead of 1. */
      update() {
        this.ticking = false;
        this.frame = null;
        if (!this.stage) return;

        var rect = this.stage.getBoundingClientRect();
        var fallback = window.innerHeight || document.documentElement.clientHeight;
        var pinH = this.pinned ? this.pinned.getBoundingClientRect().height : fallback;
        var travel = rect.height - pinH;

        /* Stage length equal to the hero height means there is nothing to
           scroll through and progress can never leave 0. Anything that starts
           hidden and is revealed by progress would then stay hidden for good,
           so say so in a class and let the CSS pin those to their resting
           state. */
        if (travel <= 1) {
          this.classList.add('is-static');
          this.style.setProperty('--p', '0');
          this.pWritten = true;
          return;
        }
        this.classList.remove('is-static');

        var p = -rect.top / travel;
        if (p < 0) p = 0;
        if (p > 1) p = 1;

        this.style.setProperty('--p', p.toFixed(4));
        this.pWritten = true;
      }

      /* ---- display heading -----------------------------------------------
         One span per unit, each carrying --s: its position from 0 to 1. The
         CSS turns that into the unit's slice of the reveal window. There is no
         measuring here, so unlike the line split in stat-reveal.js this never
         needs redoing on resize.

         The visible text is aria-hidden and a plain copy sits beside it,
         because inline-block letters make some screen readers spell the word
         out.
         ------------------------------------------------------------------ */

      splitDisplay() {
        var el = this.display;
        if (!el) return;

        var source = el.dataset.text || el.textContent.replace(/\s+/g, ' ').trim();
        el.dataset.text = source;
        if (!source) return;

        var mode = el.dataset.splitBy || 'letters';
        /* a very long heading animated letter by letter is a lot of elements
           restyled every frame for very little extra effect */
        if (mode === 'letters' && source.length > 60) mode = 'words';

        var frag = document.createDocumentFragment();
        var spans = [];
        var i;

        if (mode === 'letters') {
          var words = source.split(' ').filter(Boolean);
          words.forEach(function (word, w) {
            var wordEl = document.createElement('span');
            wordEl.className = 'cine-hero__word';
            for (var c = 0; c < word.length; c++) {
              var charEl = document.createElement('span');
              charEl.className = 'cine-hero__char';
              charEl.textContent = word.charAt(c);
              wordEl.appendChild(charEl);
              spans.push(charEl);
            }
            frag.appendChild(wordEl);
            if (w < words.length - 1) frag.appendChild(document.createTextNode(' '));
          });
        } else {
          var units = mode === 'words' ? source.split(' ').filter(Boolean) : [source];
          for (i = 0; i < units.length; i++) {
            var unitEl = document.createElement('span');
            unitEl.className = 'cine-hero__char';
            unitEl.textContent = units[i];
            frag.appendChild(unitEl);
            spans.push(unitEl);
            if (mode === 'words' && i < units.length - 1) frag.appendChild(document.createTextNode(' '));
          }
        }

        if (!spans.length) return;

        var denom = spans.length > 1 ? spans.length - 1 : 1;
        for (i = 0; i < spans.length; i++) {
          spans[i].style.setProperty('--s', (i / denom).toFixed(4));
        }

        el.textContent = '';
        el.appendChild(frag);
        el.classList.add('is-split');
      }

      /* ---- shrink to fit -------------------------------------------------
         A single line held at a fixed share of the screen width overflows as
         soon as the merchant types a long heading, and the hero clips it: at
         the 16vw mobile default, a seventeen character heading measured 510px
         wide inside a 343px column. So measure the line once and write the
         ratio that makes it span the column exactly. The ratio only ever
         shrinks, so the size setting stays the largest the line will get.

         Skipped entirely when the merchant allowed wrapping: a wrapping line
         never needs shrinking, and measuring one still returned 0.9692 on sub
         pixel overflow, quietly making the heading 3% smaller than asked for.

         The wrap's width does not change when the ratio does, only its height,
         so guarding on width is what keeps the ResizeObserver from feeding
         itself.
         ------------------------------------------------------------------ */

      fitDisplay(force) {
        var el = this.display;
        var wrap = this.displayWrap;
        if (!el || !wrap) return;

        if (!el.classList.contains('cine-hero__display--one-line')) {
          this.style.setProperty('--disp-fit', '1');
          this.fitDone = true;
          return;
        }

        var width = Math.round(wrap.clientWidth);
        if (!width) return;
        if (!force && this.fitDone && width === this.fitWidth) return;
        this.fitWidth = width;

        /* measure at the configured size, not at whatever ratio is on there
           now */
        this.style.setProperty('--disp-fit', '1');
        var avail = el.clientWidth;
        var natural = el.scrollWidth;
        if (!avail || !natural) return;

        var ratio = avail / natural;
        if (ratio > 1) ratio = 1;
        this.style.setProperty('--disp-fit', ratio.toFixed(4));
        this.fitDone = true;
      }

      /* ---- video ---------------------------------------------------------
         The <source> ships with data-src and no src, so nothing is fetched
         until this runs. Three reasons it may never run: a mobile still is set
         and we are on a phone, the visitor asked for Save-Data, or the stage
         never comes near the viewport.
         ------------------------------------------------------------------ */

      loadVideo() {
        if (!this.video || !this.source) return;
        if (this.source.getAttribute('src')) return;
        if (this.saveData) return;
        if (this.desktopOnlyVideo && !this.mqDesktop.matches) return;

        this.source.setAttribute('src', this.source.dataset.src);
        this.video.load();
        this.playVideo();
      }

      playVideo() {
        if (!this.video || !this.source || !this.source.getAttribute('src')) return;
        this.video.muted = true;
        var played = this.video.play();
        /* a refused autoplay is normal, not an error: the poster stays up */
        if (played && played.catch) played.catch(function () {});
      }

      pauseVideo() {
        /* off screen it is only burning battery */
        if (this.video && !this.video.paused) this.video.pause();
      }
    }
  );
}
